/**
 * Profile info — the last step before the app opens.
 *
 * Built to docs/references/08-profile-info.png and 10-profile-info-typed.png.
 *
 * The references are an older WhatsApp, but this screen has barely changed:
 * a round avatar well, one name field, a counter, Next. What HAS changed
 * elsewhere is the tab structure behind it — see BottomNav.
 *
 * The permission prompts in reference 09 are deliberately not built here.
 * Asking for contacts and media the moment someone arrives is the pattern
 * with the worst grant rate there is: people decline what they cannot yet
 * see a reason for. These are browser permissions anyway, and the right
 * moment to request them is the first time someone taps "attach" or "new
 * chat", where the reason is obvious. On mobile that is a native prompt and
 * the same timing applies.
 */

import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { media, userMessage } from '@zivapp/shared'
import { useAuth } from '../context/index.js'
import ThemeToggle from '../components/ui/ThemeToggle.jsx'
import './ProfileSetup.css'

const NAME_MAX = 25

export default function ProfileSetup () {
  const navigate = useNavigate()
  const { user, updateProfile } = useAuth()
  const fileRef = useRef(null)

  const [name, setName] = useState(user?.displayName ?? '')
  const [photo, setPhoto] = useState(null)       // object URL for the preview
  const [file, setFile] = useState(null)
  const [isSaving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const remaining = NAME_MAX - name.length
  const canSubmit = name.trim().length > 0 && !isSaving

  function pickPhoto (event) {
    const chosen = event.target.files?.[0]
    if (!chosen) return
    if (!chosen.type.startsWith('image/')) {
      setError('Choose an image file.')
      return
    }
    setError(null)
    setFile(chosen)
    // Revoked on replace so a long session does not leak object URLs.
    setPhoto(previous => {
      if (previous) URL.revokeObjectURL(previous)
      return URL.createObjectURL(chosen)
    })
  }

  async function onSubmit (event) {
    event.preventDefault()
    if (!canSubmit) return

    setSaving(true)
    setError(null)
    try {
      /*
       * The photo is uploaded now.
       *
       * It used to be previewed and then silently dropped — you chose a
       * picture, watched it appear in the well, pressed Next, and it was
       * never sent anywhere. Reported as "I uploaded my profile picture
       * during registration but I can't see it", which is exactly what
       * happened: the preview was an object URL that died with the page.
       *
       * The upload is not allowed to cost you the account. If it fails,
       * the name still saves and you still get in — a picture is worth
       * far less than being stuck on the setup screen.
       */
      let avatarUrl
      if (file) {
        try {
          const uploaded = await media.upload(file)
          avatarUrl = uploaded.url
        } catch {
          setError('Your photo could not be uploaded, but your name was saved. You can add it later in Profile.')
        }
      }

      await updateProfile({
        displayName: name.trim(),
        ...(avatarUrl ? { avatarUrl } : {})
      })
      // Permissions come next (docs/references/09), not the chat list.
      navigate('/permissions', { replace: true })
    } catch (err) {
      setError(userMessage(err))
      setSaving(false)
    }
  }

  return (
    <main className="ps">
      <ThemeToggle className="ps__theme" />

      <form className="ps__card" onSubmit={onSubmit}>
        <h1 className="ps__title">Profile info</h1>
        <p className="ps__sub">Please provide your name and an optional profile photo</p>

        <button
          type="button"
          className="ps__avatar"
          onClick={() => fileRef.current?.click()}
          aria-label={photo ? 'Change profile photo' : 'Add a profile photo'}
        >
          {photo
            ? <img className="ps__avatar-img" src={photo} alt="" />
            : (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M4 8.5h3l1.4-2h7.2l1.4 2h3a1.5 1.5 0 0 1 1.5 1.5v8a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 18v-8A1.5 1.5 0 0 1 4 8.5Z"
                  stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
                />
                <circle cx="12" cy="14" r="3.4" stroke="currentColor" strokeWidth="1.6" />
                <path d="M19 4.5v4M17 6.5h4" stroke="currentColor" strokeWidth="1.6"
                  strokeLinecap="round" />
              </svg>
            )}
        </button>

        <input
          ref={fileRef}
          className="ps__file"
          type="file"
          accept="image/*"
          onChange={pickPhoto}
          tabIndex={-1}
        />

        <div className="ps__field">
          <label className="ziv-sr-only" htmlFor="ps-name">Your name</label>
          <input
            id="ps-name"
            className="ps__name"
            value={name}
            onChange={e => setName(e.target.value.slice(0, NAME_MAX))}
            placeholder="Type your name here"
            maxLength={NAME_MAX}
            autoComplete="name"
            autoFocus
          />
          <span className="ps__count" aria-hidden="true">{remaining}</span>
        </div>

        {error && <p className="ps__error" role="alert">{error}</p>}

        <button type="submit" className="ps__next" disabled={!canSubmit}>
          {isSaving ? 'Saving…' : 'Next'}
        </button>

        {file && (
          <p className="ps__note">
            Your photo and name are saved to your account.
          </p>
        )}
      </form>
    </main>
  )
}
